import { StaticImage } from "gatsby-plugin-image"

import { blogUrl } from "../../services/blogLinks"

const khaleejTimesData = {
  color: "#f5f7fa",
  heroTitle: `How Khaleej Times scaled its event marketing strategy with Premagic`,
  heroImage: (
    <StaticImage
      src="../../images/case-study/khaleej-times/banner.png"
      alt="khaleej-times-uae-media-event-photo-case-study.png"
    />
  ),
  metrics: {
    quote: `A comprehensive case study on transforming traditional event marketing through personalized photo distribution and attendee-generated content.<br/><br/><br/><div style='text-align:left;'><b>Challenge:</b> UAE's leading English daily needed to enhance attendee engagement across diverse event formats while maintaining brand control and data security.<br/><br/><b>Solution:</b> Implemented Premagic's organic event marketing platform featuring real-time photo distribution, advocacy posters, and sponsor integration.</div>`,
  },
  //   images: [
  //     {
  //       img: (
  //         <StaticImage
  //           src="../../images/case-study/khaleej-times/hero.png"
  //           alt="Sponsors"
  //         />
  //       ),
  //     },
  //     {
  //       img: (
  //         <StaticImage
  //           src="../../images/case-study/khaleej-times/gallery.png"
  //           alt="AGC"
  //         />
  //       ),
  //     },
  //     {
  //       img: (
  //         <StaticImage
  //           src="../../images/case-study/khaleej-times/avatars.png"
  //           alt="Avatars"
  //         />
  //       ),
  //     },
  //   ],
  gallery: (
    <StaticImage
      src="../../images/case-study/khaleej-times/collage.png"
      alt="khaleej-times-uae-media-event-photo-case-study-gallery"
    />
  ),
  caseStudyData: [
    {
      title: "About Khaleej Times",
      info1: `<a href='https://www.khaleejtimes.com/'>Khaleej Times</a>  is the UAE’s oldest English-language daily, with a strong presence in journalism, events, and media. Known for its credibility and expansive reach, the brand regularly hosts B2B conferences, thought-leadership panels, and partner-led forums. As their events portfolio grew, the team began looking for smarter ways to <b>deepen attendee engagement, without compromising on data privacy or brand experience.</b>`,
    },
    {
      title: "The starting point: One event, one experience, one decision",
      info1: `It all started when <b>Nilesh Devadia, Director of Business Development</b> at Khaleej Times, attended an event and noticed a fellow attendee receiving personalised photos in real-time. Curious about the experience, he inquired and was introduced to Premagic. Simultaneously, their event partners were encouraging the Khaleej Times team to try Premagic, particularly for its event marketing posters.<br/><br/>What started as a single-use trial turned into something bigger. After seeing the impact of AI-powered engagement firsthand, Khaleej Times chose to use Premagic at every event they hosted after the pilot.`,
    },
    {
      title: "The Challenge",
      subheading: `Make events more engaging, with zero compromise on privacy`,
      info1: `According to <b>Mohammad Hegazy, Senior Conference Producer at Khaleej Times</b>, their team hadn’t been using dedicated tools for photo distribution, attendee referrals, or sponsor visibility. But their goal was clear: engagement needed to go up, without sacrificing security or increasing operational effort.`,
      info2: `<b>Their wishlist included:</b><br/><br/><ul style='list-style-type: disc; padding-left: 20px'><li><b>Real-time photo sharing</b> for attendees</li><li>Personalized marketing posters <b>create pre-event buzz</b> and drive referral marketing</li><li>A clear way to <b>track sponsor engagement</b></li><li>A tool that would work across <b>both large expos and intimate conferences</b></li><br/><br/></ul>They were excited about features like AI-powered photo distribution, advocacy posters, and sponsor support but also had a few concerns:<br/><br/><ul style='list-style-type: disc; padding-left: 20px'><li>Could all attendees be captured and given equal experience?</li><li>Would Premagic-certified photographers meet expectations?</li><li>Would avatars feel out of place at professional events?</li><li>Could the platform deliver consistent value across different formats?</li></ul>`,
    },
    {
      title: "The Solution",
      subheading: `A platform that adapts to every event format`,
      info1: `At <b>Khaleej Times’ Future Workforce Summit</b>, the first event powered by Premagic, <b>every initial concern was quickly resolved.</b><br/><br/>From the moment the event began, attendees were not just present—they were actively engaged.<br/><br/>The buzz had already started before the doors opened. In the days leading up to the event, dozens of attendees announced their participation by <b>sharing personalized</b> <a href='${blogUrl("attendingPosters")}'>“I’m attending” posters</a> on LinkedIn and Instagram. These auto-generated posters, branded with the event messaging and theme, created a wave of <b>pre-event visibility, peer discovery, and FOMO</b> across networks.<br/><br/>Once onsite, the momentum only grew. Within hours, hundreds of attendees received their <a href='${blogUrl("photoGalleries")}'>personalized photo galleries</a>, curated using facial recognition and packed with social-ready content. The process was instant, seamless, and a hit with the crowd.<br/><br/>Photos were shared on social media in real time, flooding timelines with branded event moments—complete with pre-filled captions, hashtags, and mentions. It created an immediate sense of excitement and visibility, not just for the event, but for the Khaleej Times brand.`,
      info2: "The following features were implemented:",
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/khaleej-times/mohammed.png"
            alt="khaleej-times-uae-media-event-photo-case-study-testimonial"
          />
        ),
        message: `“It was a new idea that we implemented at Khaleej Times. And so far, everyone is so satisfied. The feedback from the audience and speakers was really amazing. So we’re looking forward to having Premagic power our future events as well.”`,
        name: "Mohammed Hegazy",
        info: ` Senior Conference Producer<br />Khaleej Times`,
      },
      //   image: (
      //     <StaticImage
      //       src="../../images/case-study/khaleej-times/gallery.png"
      //       alt="gallery"
      //     />
      //   ),
    },
    {
      subheading: "1. Event marketing posters that created FOMO",
      info1: `Posters turned out to be a breakout success, generating a lot of FOMO among the audience. Sleek, LinkedIn-ready visuals auto-filled attendee details from LinkedIn and were shared with editable captions and hashtags. Each was linked to the event registration page.`,
      info2: `At the Future Workforce Summit alone, <b>nearly 80 attendees posted “I’m Attending” posters, which drove over 690 referrals.</b> Posters generated a lot of FOMO—amplifying reach while maintaining brand consistency.<br/><br/>Explore <a href='https://premagic.com/create-event-marketing-posters/?utm_source=website&utm_medium=casestudy&utm_id=KT'>Marketing Posters</a>`,
      imagePosition: "center",
      image: (
        <StaticImage
          src="../../images/case-study/khaleej-times/posters.png"
          alt="khaleej-times-uae-media-event-photo-case-study-fomo"
        />
      ),
    },
    {
      subheading: "2. Personalized attendee galleries",
      image: (
        <StaticImage
          src="../../images/case-study/khaleej-times/gallery.png"
          alt="khaleej-times-uae-media-event-photo-case-study-gallery"
        />
      ),
      imagePosition: "center",
      info1: `At the Future Workforce Summit, the attendee experience was at the center of it all.<br/><br/>Premagic-certified photographers covered the event end-to-end—ensuring every important moment was captured with quality and consistency. While clients can always use their own photographers, we’ve found that the experience is even smoother when our trained team is involved.`,

      info2: `Using facial recognition, each attendee received a personal gallery of their event photos, delivered instantly to their WhatsApp and email.<br/><br/>Every photo was watermarked with the Khaleej Times logo, maintaining consistent branding.<br/><br/>These AI-powered galleries also included social-ready content, making it easy for attendees to share their photos online.`,
    },
    {
      //   title: "Results: Transforming attendees into brand advocates",
      subheading: `3. AGC prompts that turned attendees into event marketers`,
      info1: `With editable, AI-assisted captions and hashtags, attendees were encouraged to share their photos directly to their LinkedIn and Instagram pages. This turned out to be a huge amplifier for the event—dozens of people tagged Khaleej Times in real-time, extending event visibility and generating organic buzz.<br/><br/>`,
      info2: `The <b>AGC (Attendee-Generated Content) performance</b> spoke for itself:<br/><br/><ul style='list-style:disc; padding-left:20px'><li>Dozens of authentic, unpaid mentions and event tags</li><li>Organic amplification of key speakers, sponsors, and moments</li><li>A lasting social footprint well beyond the event day</li></ul>`,
      imagePosition: "center",
      image: (
        <StaticImage
          src="../../images/case-study/khaleej-times/agc.png"
          alt="khaleej-times-uae-media-event-photo-case-study-agc"
        />
      ),
    },

    {
      subheading: "4. Sponsor content that’s trackable",
      info2: `Khaleej Times also used Premagic’s sponsorship module to give sponsors more visibility with watermarked photos, video ads and logos. Sponsor logos and CTA buttons were integrated into both the registration page and the attendee galleries, offering real-time click tracking and measurable engagement. Sponsors reported high satisfaction and value from the added exposure.<br><br/>Explore <a href='https://premagic.com/sponsored-content/?utm_source=website&utm_medium=casestudy&utm_id=KT'>Sponsor Module</a>`,
      image: (
        <StaticImage
          src="../../images/case-study/khaleej-times/sponsors.png"
          alt="khaleej-times-uae-media-event-photo-case-study-sponsor"
        />
      ),
    },
    {
      subheading: "5. AI Avatars that made things memorable",
      info2: `Initially met with some hesitation, avatars became a surprise favorite. Attendees could generate and share fun, AI-generated versions of themselves, ranging from Scottish bagpipers to Arabian royalty. Hundreds of these were created, adding a personal, creative touch to the professional setting.<br/><br/>Explore <a href='https://premagic.com/avatar-studio/?utm_source=website&utm_medium=casestudy&utm_id=KT'>Avatar Studio </a>`,

      image: (
        <StaticImage
          src="../../images/case-study/khaleej-times/avatars.png"
          alt="khaleej-times-uae-media-event-photo-case-study-avatar"
        />
      ),
    },
    {
      title: "Built for both expos and curated conferences",
      info1: `Premagic delivers value across all formats.<br/><br/>At large expos, our tools help manage scale and speed. Attendees receive their galleries within minutes. Branding is consistent. Social sharing is seamless.<br/><br/>At smaller, high-intent events, the focus shifts to quality. AGC, marketing posters, and personalized galleries ensure every attendee feels seen, heard, and compelled to share.<br/><br/>Whether you’re hosting 3000 or 300, Premagic scales with you. `,
      //   image: (
      //     <StaticImage
      //       src="../../images/case-study/khaleej-times/avatars.png"
      //       alt="gallery"
      //     />
      //   ),
    },
    {
      title: "A partnership built on outcomes",
      testimonial: {
        img: (
          <StaticImage
            src="../../images/case-study/khaleej-times/mohammed.png"
            alt="khaleej-times-uae-media-event-photo-case-study-testimonial"
          />
        ),
        message: `“Thank you so much, Premagic, for being with us on this edition. Without you, we would not have reached this level of attendee satisfaction.  We are looking forward to having you at our future events.”`,
        name: "Mohammed Hegazy",
        info: `Senior Conference Producer<br />Khaleej Times`,
      },
      info1: `The Khaleej Times team didn’t just like the experience; they retained it. After the first event, they used Premagic for every event they hosted. Their team now relies on us for end-to-end event engagement—from sponsor activation and attendee delight to social media buzz and brand recall.<br/>`,
      info2: `For event organizers considering similar transformations, the key insight is clear: when attendees are genuinely delighted with their experience, they become powerful advocates for your brand. The technology simply enables and amplifies this natural tendency.<br/><br/>The partnership between Khaleej Times and Premagic proves that innovative event marketing solutions can deliver measurable results across any event format—from massive trade shows to intimate executive roundtables.<br/><br/><b>Ready to transform your event marketing strategy?</b>`,
    },
  ],
  isCTAActive: true,
}

export default khaleejTimesData
